import type {
  AnswerJobQuestionsInput,
  ClientJob,
  ClientJobListItem,
  ClientJobListQuery,
  ClientOverview,
  ClientProposal,
  ClientStats,
  CloseJobInput,
  CreatedUpload,
  DeclineProposalInput,
  EditJobInput,
  JobAttachmentInput,
  Paginated,
  UploadInfo,
} from '@hemmafixa/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ApiError } from '@/lib/api';
import { api } from '@/lib/api-client';

// The client's own jobs (P3-09) and the proposals on them (P4-07).

const jobsKey = ['client', 'jobs'] as const;

type MyJobsParams = Partial<Pick<ClientJobListQuery, 'filter' | 'q' | 'page' | 'pageSize'>>;

function queryString(params: MyJobsParams) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

export function useMyJobs(params: MyJobsParams = {}) {
  return useQuery({
    queryKey: [...jobsKey, 'list', params],
    queryFn: () => api<Paginated<ClientJobListItem>>(`/client/jobs${queryString(params)}`),
    placeholderData: keepPreviousData,
  });
}

/** One of the client's jobs; null for a wrong or old link. */
export function useMyJob(id: string) {
  return useQuery({
    queryKey: [...jobsKey, 'job', id],
    queryFn: async () => {
      try {
        return await api<ClientJob>(`/client/jobs/${id}`);
      } catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.status === 400)) return null;
        throw error;
      }
    },
  });
}

function useJobChange<TInput>(id: string, request: (input: TInput) => Promise<ClientJob>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (job) => {
      queryClient.setQueryData([...jobsKey, 'job', id], job);
      void queryClient.invalidateQueries({ queryKey: [...jobsKey, 'list'] });
    },
  });
}

export const useEditMyJob = (id: string) =>
  useJobChange<EditJobInput>(id, (input) =>
    api<ClientJob>(`/client/jobs/${id}`, { method: 'PATCH', body: input }),
  );

export const useAnswerMyJob = (id: string) =>
  useJobChange<AnswerJobQuestionsInput['answers']>(id, (answers) =>
    api<ClientJob>(`/client/jobs/${id}/answers`, { method: 'PATCH', body: { answers } }),
  );

/** Proposals on one of the client's jobs, oldest first; contact details are hidden until accepted. */
export function useJobProposals(jobId: string, enabled = true) {
  return useQuery({
    queryKey: [...jobsKey, 'proposals', jobId],
    queryFn: () => api<ClientProposal[]>(`/client/jobs/${jobId}/proposals`),
    enabled,
  });
}

/** Accepting shows both sides' contact details and opens a conversation (P5-02). */
export function useAcceptProposal(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<ClientProposal>(`/client/proposals/${id}/accept`, { method: 'POST' }),
    onSuccess: (accepted) =>
      queryClient.setQueryData<ClientProposal[]>([...jobsKey, 'proposals', jobId], (current) =>
        current?.map((proposal) => (proposal.id === accepted.id ? accepted : proposal)),
      ),
  });
}

/** Gives the job to an accepted proposal, or cancels it (P5-05); the job and its proposals reload. */
export function useCloseJob(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CloseJobInput) =>
      api<void>(`/client/jobs/${jobId}/close`, { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobsKey });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useDeclineProposal(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: DeclineProposalInput & { id: string }) =>
      api<ClientProposal>(`/client/proposals/${id}/decline`, { method: 'POST', body: input }),
    onSuccess: (declined) =>
      queryClient.setQueryData<ClientProposal[]>([...jobsKey, 'proposals', jobId], (current) =>
        current?.map((proposal) => (proposal.id === declined.id ? declined : proposal)),
      ),
  });
}

/** Adding and removing photos and documents while the job is in review; the job reloads afterwards. */
export function useJobAttachments(jobId: string) {
  const queryClient = useQueryClient();
  return useMemo(
    () => ({
      create: (input: JobAttachmentInput) =>
        api<CreatedUpload>(`/client/jobs/${jobId}/attachments`, { method: 'POST', body: input }),
      complete: (uploadId: string) =>
        api<UploadInfo>(`/client/jobs/${jobId}/attachments/${uploadId}/complete`, { method: 'POST' }),
      remove: (uploadId: string) =>
        api<void>(`/client/jobs/${jobId}/attachments/${uploadId}`, { method: 'DELETE' }),
      refresh: () => void queryClient.invalidateQueries({ queryKey: [...jobsKey, 'job', jobId] }),
    }),
    [jobId, queryClient],
  );
}

/** Start-page numbers (P5-07); refreshed every minute while the page is open */
export function useClientOverview() {
  return useQuery({
    queryKey: ['client', 'overview'],
    queryFn: () => api<ClientOverview>('/client/overview'),
    refetchInterval: 60_000,
  });
}

/** Start-page charts (UI-05): the last six months and totals by status */
export function useClientCharts() {
  return useQuery({
    queryKey: ['client', 'charts'],
    queryFn: () => api<ClientStats>('/client/stats'),
    staleTime: 5 * 60_000,
  });
}
